// Smart Column Filter Types

export type FilterOperator = 'AND' | 'OR';

export type FilterField = 
  | 'sender'        // Email address of sender
  | 'senderName'    // Display name of sender
  | 'recipient'     // Email address in To/CC
  | 'subject'       // Subject line
  | 'body'          // Body text
  | 'hasAttachment' // Boolean
  | 'isRead'        // Boolean
  | 'isFlagged'     // Boolean (importance=high)
  | 'priority'      // 'high' | 'normal' | 'low'
  | 'hasReminder'   // Boolean (flag followUp)
  | 'category'      // Category/label name
  | 'date'          // Received date
  | 'unread';       // Alias for !isRead

export type ConditionOperator = 
  | 'equals'      // Exact match
  | 'contains'    // Substring match
  | 'startsWith'  // Prefix match
  | 'endsWith'    // Suffix match
  | 'before'      // Date comparison
  | 'after'       // Date comparison
  | 'within';     // Date: within last X days

export interface FilterCondition {
  field: FilterField;
  operator: ConditionOperator;
  value: string | boolean | number;
  negate?: boolean; // NOT modifier
}

export interface FilterGroup {
  operator: FilterOperator;
  conditions: (FilterCondition | FilterGroup)[];
}

export type FilterConfig = FilterGroup;

// Type guard
export function isFilterGroup(obj: FilterCondition | FilterGroup): obj is FilterGroup {
  return 'operator' in obj && 'conditions' in obj && Array.isArray((obj as FilterGroup).conditions);
}

// Human-readable field names
export const FILTER_FIELD_LABELS: Record<FilterField, string> = {
  sender: 'Absender (E-Mail)',
  senderName: 'Absender (Name)',
  recipient: 'Empfänger',
  subject: 'Betreff',
  body: 'Inhalt',
  hasAttachment: 'Hat Anhang',
  isRead: 'Gelesen',
  isFlagged: 'Markiert',
  priority: 'Priorität',
  hasReminder: 'Hat Erinnerung',
  category: 'Kategorie',
  date: 'Datum',
  unread: 'Ungelesen',
};

// Operators per field type
export const FIELD_OPERATORS: Record<FilterField, ConditionOperator[]> = {
  sender: ['equals', 'contains', 'startsWith', 'endsWith'],
  senderName: ['equals', 'contains', 'startsWith', 'endsWith'],
  recipient: ['equals', 'contains', 'startsWith', 'endsWith'],
  subject: ['equals', 'contains', 'startsWith', 'endsWith'],
  body: ['contains'],
  hasAttachment: ['equals'],
  isRead: ['equals'],
  isFlagged: ['equals'],
  priority: ['equals'],
  hasReminder: ['equals'],
  category: ['equals', 'contains'],
  date: ['before', 'after', 'within'],
  unread: ['equals'],
};

// Operator labels
export const OPERATOR_LABELS: Record<ConditionOperator, string> = {
  equals: 'ist',
  contains: 'enthält',
  startsWith: 'beginnt mit',
  endsWith: 'endet mit',
  before: 'vor',
  after: 'nach',
  within: 'innerhalb der letzten',
};

// Boolean fields
export const BOOLEAN_FIELDS: FilterField[] = ['hasAttachment', 'isRead', 'isFlagged', 'hasReminder', 'unread'];

// Priority values
export const PRIORITY_VALUES = [
  { value: 'high', label: 'Hoch' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Niedrig' },
];

// Date "within" presets
export const DATE_WITHIN_PRESETS = [
  { value: 1, label: '1 Tag' },
  { value: 3, label: '3 Tage' },
  { value: 7, label: '7 Tage' },
  { value: 14, label: '14 Tage' },
  { value: 30, label: '30 Tage' },
  { value: 90, label: '90 Tage' },
];
